// Virtual net billing (εικονικός ταυτοχρονισμένος συμψηφισμός), Greece.
// ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 art. 2 par. 13, art. 5α par. 3-4, art. 8.
// Mirrors backend BESS_feasibility/Configuration/virtual_net_billing.py.
//
// The station injects all of its output, the supplies draw all of their
// consumption from the grid, and the two are netted in the accounts hour by
// hour, supply by supply with the allocation percentages. A netted kWh saves
// the supplier's energy price less the charge for ΛΠ1-3 and losses.
//
// Every sentence below is English and number-free; the Greek is in
// i18n/reviewedGreekScreens.js, keyed by these constants.

export const VNB_MODE = "virtual_net_billing";

export const VNB_OPTION_LABEL =
  "Virtual net billing — the station injects everything and is netted against the supplies";
export const VNB_OPTION_DESCRIPTION =
  "The station is not connected to the supplies it serves: it injects all of its output, the " +
  "supplies draw all of their consumption from the grid, and the two are netted in the accounts " +
  "hour by hour. The injected energy is split among the supplies by fixed allocation " +
  "percentages; each supply nets the smaller of its consumption and its share. A netted kWh " +
  "saves the supplier's energy price less the charge for ΛΠ1-3 and losses; the regulated " +
  "charges stay on all consumption and there is no capacity-charge saving. The battery may not " +
  "charge from the grid.";

export const VNB_SELF_CONSUMPTION_TEXT =
  "Under virtual net billing the station's output is netted against the supplies' consumption " +
  "hour by hour. A netted kWh saves the supplier's energy price less the charge for ΛΠ1-3 and " +
  "losses; the regulated charges stay on all consumption.";
export const VNB_SECTION_TITLE = "Virtual net billing — settlement";
export const VNB_SECTION_TEXT =
  "The supplies to be netted, their allocation percentages and their consumption are entered " +
  "on the Feasibility > Technical page, under Electricity consumption.";
export const VNB_SUPPLIES_TITLE = "Virtual net billing — supplies to be netted";
export const VNB_SUPPLIES_TEXT =
  "Enter the supplies to be netted, with their allocation percentages from the contract " +
  "appendix. With more than one supply, import each supply's hourly consumption: the project's " +
  "consumption is then set to their sum.";
export const VNB_SUM_LABEL = "Sum of the supplies' files (virtual net billing)";
export const VNB_SUM_LOCKED_NOTE =
  "This is the project's consumption: the hourly sum of the supplies' files, set automatically. " +
  "To change it, replace or remove a supply's file above.";
export const VNB_SUM_PENDING_NOTE =
  "The project's consumption will be the sum of the supplies' files once every supply has its file.";
export const VNB_CHARGE_LABEL = "Charge on netted energy for ΛΠ1-3 and losses (EUR/MWh)";
export const VNB_CHARGE_HELPER =
  "The supplier bills every netted kWh for the uplift accounts ΛΠ1-3 and the losses. Enter it " +
  "on the same basis as the tariff: before VAT for an itemized tariff, as billed otherwise. " +
  "ΑΔΜΗΕ publishes the ΛΠ every month.";
export const VNB_SURPLUS_LABEL = "The surplus is paid at the day-ahead price";
export const VNB_SURPLUS_HELPER =
  "Clear it if producing RES power is the self-consumer's main business, or if the " +
  "self-consumer chose not to be compensated: the station still injects everything, but the " +
  "surplus is not paid.";
export const VNB_SUPPLY_NAME_LABEL = "Supply";
export const VNB_SUPPLY_SHARE_LABEL = "Allocation (%)";
export const VNB_ADD_SUPPLY = "Add supply";
export const VNB_REMOVE_SUPPLY = "Remove supply";
export const VNB_SHARES_TOTAL_LABEL = "Allocation total";
export const VNB_SUPPLY_FILE_TITLE = "Hourly consumption of the supply";
export const VNB_SINGLE_SUPPLY_NOTE =
  "With one supply, the project's consumption below is the consumption of that supply.";
export const VNB_SUM_SET_NOTE =
  "The project's consumption is the sum of the supplies' files.";
export const VNB_LOCKED_STREAMS_NOTE =
  "Set by virtual net billing: peak shaving, charging in the cheap tariff hours, the fixed " +
  "feed-in and net billing do not apply. The supplies draw all of their consumption from the " +
  "grid, the battery charges from the station's PV only, and the surplus is settled under " +
  "virtual net billing.";
export const VNB_ASSUMPTIONS_NOTE =
  "The study nets per hour; the station and the supplies are taken to be at the same voltage " +
  "level; every supply is billed at the project's tariff.";
export const VNB_ASSUMPTIONS_NOTE_VOLTAGE =
  "The study nets per hour; a supply at another voltage level than the station nets the " +
  "allocated energy times its reduction factor; every supply is billed at the project's tariff.";

// Voltage levels (ΚΥΑ art. 7). Mirrors backend virtual_net_billing.py.
export const VNB_VOLTAGE_TITLE = "Voltage level of the station and the supplies";
export const VNB_VOLTAGE_TEXT =
  "A supply connected at another voltage level than the station nets the allocated energy " +
  "times a reduction factor for the network losses (ΚΥΑ art. 7). Leave the station's level " +
  "unset when the station and the supplies are at the same level.";
export const VNB_STATION_VOLTAGE_LABEL = "Voltage level of the station";
export const VNB_SUPPLY_VOLTAGE_LABEL = "Voltage level";
export const VNB_VOLTAGE_SAME_OPTION = "Same as the supplies (factor 1)";
export const VNB_VOLTAGE_OPTIONS = [
  { value: "LV", label: "Low voltage (LV)" },
  { value: "MV", label: "Medium voltage (MV)" },
  { value: "HV", label: "High voltage (HV)" },
];
export const VNB_LOSS_MV_LABEL = "Loss uplift factor ΣΠΑ ΜΤ (%)";
export const VNB_LOSS_LV_LABEL = "Loss uplift factor ΣΠΑ ΧΤ (%)";
export const VNB_LOSS_HELPER =
  "Set every year by ΡΑΑΕΥ and published by ΔΕΔΔΗΕ. Left blank, the values of ΡΑΑΕΥ decision " +
  "Ε-224/2026 apply.";
export const VNB_REDUCTION_FACTOR_LABEL = "Reduction factor";
export const VNB_VOLTAGE_SURPLUS_NOTE =
  "Each supply nets the smaller of its consumption and its allocated energy times the factor. " +
  "What the factor removes from netting is surplus (ΚΥΑ art. 2 par. 21).";
export const VNB_ERR_LOSS = "The loss uplift factors ΣΠΑ must be between 0 and 100%.";

// Agreed power per supply: read only by «Check assumptions» (ΚΥΑ art. 4 par. 5,
// art. 8 par. 2); never sent to the run.
export const VNB_SUPPLY_AGREED_LABEL = "Agreed power (kVA)";
export const VNB_OPTIONAL_PLACEHOLDER = "optional";

// Suggested allocation percentages (backend virtual_net_billing.suggest_allocation).
export const VNB_SUGGEST_TITLE = "Best allocation percentages";
export const VNB_SUGGEST_TEXT =
  "Calculates the allocation percentages that net the most energy in a year, from the supplies' " +
  "files and the PV's reference-year production, up to the connection limit. Nothing changes " +
  "until you apply them.";
export const VNB_SUGGEST_BUTTON = "Calculate the best percentages";
export const VNB_SUGGEST_RUNNING = "Calculating…";
export const VNB_SUGGEST_APPLY = "Apply the suggested percentages";
export const VNB_SUGGEST_APPLIED = "The suggested percentages are applied.";
export const VNB_SUGGEST_COL_CURRENT = "Current (%)";
export const VNB_SUGGEST_COL_SUGGESTED = "Suggested (%)";
export const VNB_SUGGEST_COL_NETTED_CURRENT = "Netted, current (MWh)";
export const VNB_SUGGEST_COL_NETTED_SUGGESTED = "Netted, suggested (MWh)";
export const VNB_SUGGEST_TOTAL = "Total";
export const VNB_SUGGEST_GAIN = "Additional netted energy per year";
export const VNB_SUGGEST_BEST_ALREADY = "The current percentages already net the most energy.";
export const VNB_SUGGEST_NOTE =
  "Calculated for the first year, on the PV production without the battery; with a battery the " +
  "best percentages can differ slightly. The percentages of the contract appendix are the " +
  "self-consumer's to set.";
export const VNB_SUGGEST_NO_ESTIMATE =
  "Run the PV's reference-year production estimate first (PV System Configuration → " +
  "Reference-year production → Estimate).";
export const VNB_SUGGEST_SAVE_FIRST = "Save the project first.";
export const VNB_SUGGEST_KWP_NOTE =
  "The production estimate was made for a different PV size than the project's; run the " +
  "estimate again for an exact suggestion.";
export const VNB_SUGGEST_ERROR = "The percentages could not be calculated.";

// Energy price per supply (backend virtual_net_billing / read_config
// btm_vnb_supply_values): a supply on its own supply contract nets at its own
// price less the same charge; the others follow the project's tariff.
export const VNB_SUPPLY_TARIFF_TITLE = "Virtual net billing — energy price per supply";
export const VNB_SUPPLY_TARIFF_TEXT =
  "Each supply can follow the project's tariff or its own supplier's energy price. Only the " +
  "energy price counts: the regulated charges stay on all consumption, and the charge for ΛΠ1-3 " +
  "and losses is the same for every supply.";
export const VNB_SUPPLY_TARIFF_LABEL = "Tariff";
export const VNB_SUPPLY_TARIFF_PROJECT = "The project's tariff";
export const VNB_SUPPLY_TARIFF_OWN = "Its own energy price";
export const VNB_SUPPLY_PRICE_LABEL = "Supplier energy price (EUR/kWh)";
export const VNB_SUPPLY_PRICE_HELPER_ITEMIZED =
  "Before VAT, like the energy charge of the project's itemized tariff; the same VAT applies.";
export const VNB_SUPPLY_PRICE_HELPER_TOTAL =
  "The energy price only, entered the way the project's price is entered (as billed).";
export const VNB_SUPPLY_PRICE_HELPER_DYNAMIC =
  "A fixed price for every hour, instead of the project's dynamic price.";
export const VNB_SUPPLY_TARIFF_BATTERY_NOTE =
  "The battery's charge and discharge are optimised on the project's tariff; the netting and " +
  "the money are settled with each supply's price.";
export const VNB_ERR_SUPPLY_PRICE =
  "Enter the energy price of every supply that has its own price, above zero and up to 5 EUR/kWh.";
export const VNB_AGREED_POWER_NOTE =
  "The agreed power of each supply is optional and is used only by «Check assumptions»: the " +
  "station's installed power and its injection, PV and battery together, may not exceed the sum " +
  "of the supplies' agreed power (ΚΥΑ art. 4 par. 5, art. 8 par. 2).";

export const VNB_VOLTAGE_LEVELS = ["LV", "MV", "HV"];
export const DEFAULT_LOSS_UPLIFT_MV_PCT = 4.35;
export const DEFAULT_LOSS_UPLIFT_LV_PCT = 10.37;

/** 'LV' | 'MV' | 'HV', or null for blank / unknown. */
export function voltageLevel(value) {
  const text = String(value ?? "").trim().toUpperCase();
  return VNB_VOLTAGE_LEVELS.includes(text) ? text : null;
}

/** ΣΑ of ΚΥΑ art. 7 par. 3; 1 at the same level or when either is unknown. */
export function reductionFactor(stationLevel, supplyLevel, mvPct, lvPct) {
  if (!stationLevel || !supplyLevel || stationLevel === supplyLevel) return 1;
  const mv = 1 + Number(mvPct) / 100;
  const lv = 1 + Number(lvPct) / 100;
  const pair = [stationLevel, supplyLevel].sort().join("-");
  if (pair === "HV-MV") return 1 / mv;
  if (pair === "HV-LV") return 1 / (mv * lv);
  return 1 / lv;
}

// Errors (number-free, so they translate as whole sentences).
export const VNB_ERR_CHARGE =
  "Enter the charge on netted energy for ΛΠ1-3 and losses (EUR/MWh), zero or more. Without it " +
  "the saving would be overstated.";
export const VNB_ERR_NO_SUPPLY = "Add at least one supply to be netted, with its allocation percentage.";
export const VNB_ERR_SHARE = "Every supply needs an allocation percentage above zero and up to one hundred.";
export const VNB_ERR_SHARE_SUM = "The allocation percentages must add up to 100% (ΚΥΑ art. 5α par. 4).";
export const VNB_ERR_FILES =
  "With more than one supply, import the hourly consumption of every supply.";
export const VNB_ERR_FILE_LENGTH =
  "The supplies' files do not cover the same hours. Import every file for the same year.";
export const VNB_ERR_STANDALONE =
  "Virtual net billing nets the production of a station; a standalone battery has none.";
export const VNB_ERR_DC =
  "Virtual net billing is not yet available with a hybrid inverter (DC coupling). Use AC " +
  "coupling for the battery, or run the study without the battery.";
export const VNB_ERR_COUNTRY =
  "Virtual net billing is the Greek scheme of ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 and is available " +
  "for projects in Greece only.";
export const VNB_ERR_TARIFF =
  "Virtual net billing values a netted kWh at the energy part of the price only. With a single " +
  "effective price, enter the energy share of the retail price (General page, retail tariff), " +
  "or enter the tariff itemized.";
export const VNB_ERR_SUM =
  "The project's consumption is no longer the sum of the supplies. Import the supplies' files " +
  "again so that it is set to their sum.";
export const VNB_ERR_SINGLE_STALE =
  "The project's consumption is still the sum of the supplies. With one supply, import that " +
  "supply's consumption on the Feasibility > Technical page.";
export const VNB_ERR_TRANSFORMER =
  "Virtual net billing is not yet available with the transformer and cable losses inside the " +
  "dispatch. Choose a transformer-loss mode that keeps them out of the dispatch.";

// BESS-page switches set by the regime.
export const VNB_INJECTION_HINT =
  "Locked ON by virtual net billing on the General page: the station injects all of its " +
  "output, PV and battery, up to the connection limit.";
export const VNB_INJECTION_CAPTION = "Behind-the-Meter: virtual net billing, the station injects everything.";
export const VNB_BATTERY_INJECTION_HINT =
  "Locked ON by virtual net billing on the General page: the battery injects into the grid " +
  "together with the PV, and its energy is netted against the supplies.";
export const VNB_BATTERY_INJECTION_CAPTION = "Behind-the-Meter: the battery injects with the PV.";
export const VNB_GRID_CHARGING_HINT =
  "Locked OFF by virtual net billing: storage at the station may not charge from the grid " +
  "(ΚΥΑ art. 8).";
export const VNB_GRID_CHARGING_CAPTION = "Behind-the-Meter: virtual net billing, no grid charging.";
export const VNB_INJECTION_RUN_NOTE =
  "'Grid injection at the connection point' on the BESS page will be switched ON for this " +
  "run: under virtual net billing the station injects everything.";

export function isVirtualNetBilling(btm) {
  return String(btm?.gridInjection?.mode || "allowed") === VNB_MODE;
}

let supplyCounter = 0;
export function newSupplyId() {
  supplyCounter += 1;
  return `s${Date.now().toString(36)}${supplyCounter}`;
}

export function defaultVirtualNetBilling() {
  return {
    nettedChargeEurPerMwh: "",
    surplusCompensated: true,
    supplies: [{ id: newSupplyId(), name: "1", sharePct: 100 }],
  };
}

const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return NaN;
  const number = Number(typeof value === "string" ? value.trim().replace(",", ".") : value);
  return Number.isFinite(number) ? number : NaN;
};

/** An imported profile as hourly kWh (15-minute files are summed per hour). */
export function hourlyValues(profile) {
  const values = Array.isArray(profile?.values) ? profile.values : [];
  const numbers = values.map((v) => {
    const n = toNumber(v);
    return Number.isFinite(n) ? Math.max(0, n) : 0;
  });
  const declared = String(profile?.resolution || "").toLowerCase();
  const quarter = declared === "15min" || (declared !== "hourly" && numbers.length > 8784);
  if (!quarter) return numbers;
  const hourly = [];
  for (let i = 0; i + 3 < numbers.length; i += 4) {
    hourly.push(numbers[i] + numbers[i + 1] + numbers[i + 2] + numbers[i + 3]);
  }
  return hourly;
}

/** Sum of the supplies' hourly consumption, or null when it cannot be formed. */
export function supplySum(supplies) {
  const list = Array.isArray(supplies) ? supplies : [];
  if (list.length < 2 || list.some((s) => !Array.isArray(s?.loadProfile?.values) || !s.loadProfile.values.length)) {
    return null;
  }
  const series = list.map((s) => hourlyValues(s.loadProfile));
  const length = series[0].length;
  if (!length || series.some((s) => s.length !== length)) return null;
  const sum = new Array(length).fill(0);
  series.forEach((s) => s.forEach((v, i) => { sum[i] += v; }));
  // Rounded to the Wh, which is far below any meter's resolution; avoids
  // floating noise in the stored project.
  return sum.map((v) => Math.round(v * 1000) / 1000);
}

export const VNB_SUM_SOURCE = "virtual-net-billing-supplies";

function withLoadProfile(cfg, demand, loadProfile) {
  return {
    ...cfg,
    feasibility: {
      ...(cfg.feasibility || {}),
      technical: {
        ...((cfg.feasibility || {}).technical || {}),
        demand: { ...demand, enabled: true, loadProfile },
      },
    },
  };
}

/**
 * The config with the project's consumption set to the sum of the supplies,
 * when virtual net billing has two or more supplies with files. When a supply
 * was removed and one is left, the sum it leaves behind is replaced by that
 * supply's own file. Returns the same object when nothing needs to change
 * (any other regime included).
 */
export function alignVirtualNetBillingLoad(cfg) {
  const btm = cfg?.btm;
  if (!isVirtualNetBilling(btm)) return cfg;
  const supplies = Array.isArray(btm?.virtualNetBilling?.supplies) ? btm.virtualNetBilling.supplies : [];
  const demand = cfg?.feasibility?.technical?.demand || {};
  const current = demand.loadProfile;
  if (supplies.length === 1) {
    const own = supplies[0]?.loadProfile;
    if (current?.source === VNB_SUM_SOURCE && Array.isArray(own?.values) && own.values.length) {
      return withLoadProfile(cfg, demand, { ...own, use: true });
    }
    return cfg;
  }
  const sum = supplySum(supplies);
  if (!sum) return cfg;
  const same = current?.use !== false && current?.source === VNB_SUM_SOURCE
    && Array.isArray(current?.values) && current.values.length === sum.length
    && current.values.every((v, i) => Math.abs(Number(v) - sum[i]) < 1e-9);
  if (same && demand.enabled !== false) return cfg;
  const first = (supplies[0] || {}).loadProfile || {};
  const loadProfile = {
    values: sum,
    resolution: "hourly",
    source: VNB_SUM_SOURCE,
    importedAt: new Date().toISOString(),
    use: true,
  };
  // The supplies' files are placed by date in the study year, so the sum
  // carries the first file's reading (period, year) for the page to show.
  if (first.import) loadProfile.import = { ...first.import, layout: VNB_SUM_SOURCE };
  return withLoadProfile(cfg, demand, loadProfile);
}

const lossUplift = (value, fallback) => {
  const n = toNumber(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Each supply's voltage level and reduction factor, as the run will apply them.
 * No station level: every factor is 1 (whatever a supply's own field holds).
 */
export function voltageLevels(block) {
  const src = block || {};
  const station = voltageLevel(src.stationVoltageLevel);
  const supplies = Array.isArray(src.supplies) ? src.supplies : [];
  const mvPct = lossUplift(src.lossUpliftMvPct, DEFAULT_LOSS_UPLIFT_MV_PCT);
  const lvPct = lossUplift(src.lossUpliftLvPct, DEFAULT_LOSS_UPLIFT_LV_PCT);
  const levels = supplies.map((s) => (station ? voltageLevel(s?.voltageLevel) || station : null));
  const differ = Boolean(station) && levels.some((level) => level !== station);
  return {
    station,
    differ,
    mvPct,
    lvPct,
    levels,
    factors: levels.map((level) => (differ ? reductionFactor(station, level, mvPct, lvPct) : 1)),
  };
}

/**
 * The supplies' agreed power (kVA): the sum when every supply has a value
 * above zero, and the names of those that do not.
 */
export function agreedPower(block) {
  const supplies = Array.isArray(block?.supplies) ? block.supplies : [];
  const values = supplies.map((s) => toNumber(s?.agreedPowerKva));
  const missing = supplies
    .map((s, i) => (Number.isFinite(values[i]) && values[i] > 0 ? null : String(s?.name || i + 1)))
    .filter((name) => name !== null);
  const complete = supplies.length > 0 && missing.length === 0;
  return { complete, missing, totalKva: complete ? values.reduce((a, b) => a + b, 0) : null, values };
}

/** Blocking problems of the virtual net billing block; {} when valid. */
export function validateVirtualNetBilling(btm, cfg, { standalone = false } = {}) {
  const errors = {};
  if (!isVirtualNetBilling(btm)) return errors;
  const block = btm?.virtualNetBilling || {};
  const charge = toNumber(block.nettedChargeEurPerMwh);
  if (!Number.isFinite(charge) || charge < 0) errors.vnbCharge = VNB_ERR_CHARGE;
  const supplies = Array.isArray(block.supplies) ? block.supplies : [];
  if (!supplies.length) {
    errors.vnbSupplies = VNB_ERR_NO_SUPPLY;
  } else {
    const shares = supplies.map((s) => toNumber(s?.sharePct));
    if (shares.some((v) => !Number.isFinite(v) || v <= 0 || v > 100)) {
      errors.vnbShares = VNB_ERR_SHARE;
    } else if (Math.abs(shares.reduce((a, b) => a + b, 0) - 100) > 0.01) {
      errors.vnbShares = VNB_ERR_SHARE_SUM;
    }
    if (supplies.length === 1) {
      const profile = cfg?.feasibility?.technical?.demand?.loadProfile;
      if (profile?.source === VNB_SUM_SOURCE) errors.vnbSum = VNB_ERR_SINGLE_STALE;
    }
    if (supplies.length >= 2) {
      if (supplies.some((s) => !Array.isArray(s?.loadProfile?.values) || !s.loadProfile.values.length)) {
        errors.vnbFiles = VNB_ERR_FILES;
      } else if (!supplySum(supplies)) {
        errors.vnbFiles = VNB_ERR_FILE_LENGTH;
      } else {
        const profile = cfg?.feasibility?.technical?.demand?.loadProfile;
        const sum = supplySum(supplies);
        const main = profile?.use === false ? null : hourlyValues(profile);
        const matches = main && main.length === sum.length
          && main.every((v, i) => Math.abs(v - sum[i]) <= 1e-3);
        if (!matches) errors.vnbSum = VNB_ERR_SUM;
      }
    }
  }
  const badPrice = supplies.some((s) => {
    if (s?.tariffMode !== "own") return false;
    const n = toNumber(s?.energyPriceEurKwh);
    return !Number.isFinite(n) || n <= 0 || n > 5;
  });
  if (badPrice) errors.vnbSupplyPrice = VNB_ERR_SUPPLY_PRICE;
  if (voltageLevels(block).differ) {
    const bad = ["lossUpliftMvPct", "lossUpliftLvPct"].some((key) => {
      const raw = block[key];
      if (raw === "" || raw === null || raw === undefined) return false;
      const n = toNumber(raw);
      return !Number.isFinite(n) || n < 0 || n > 100;
    });
    if (bad) errors.vnbLoss = VNB_ERR_LOSS;
  }
  if (standalone) errors.vnbSystem = VNB_ERR_STANDALONE;
  if (cfg?.bess?.topology === "dc-coupled" && String(cfg?.systemType || "") !== "Standalone PV") {
    errors.vnbSystem = VNB_ERR_DC;
  }
  // Mirrors backend transformer_chain.normalize_transformer_chain_config.
  const dets = cfg?.transformerDets || {};
  const chainMode = String(dets.chainLossMode || cfg?.chainLossMode || "off").trim().toLowerCase();
  const topology = String(dets.topology || "shared_poi").trim().toLowerCase();
  if (cfg?.hasTransformer && topology !== "none" && chainMode === "in_dispatch") {
    errors.vnbTransformer = VNB_ERR_TRANSFORMER;
  }
  const country = String(btm?.gridInjection?.country || cfg?.biddingZone || "").trim().toUpperCase();
  if (country && !country.startsWith("GR")) errors.vnbCountry = VNB_ERR_COUNTRY;
  const tariffs = cfg?.feasibility?.technical?.tariffs || {};
  const dynamic = String(tariffs.billingScheme || "").trim().toLowerCase().startsWith("dynamic");
  const itemized = !dynamic && tariffs.rateMode === "itemized";
  const shareRaw = btm?.retailTariff?.energySharePct;
  if (!dynamic && !itemized && !Number.isFinite(toNumber(shareRaw))) {
    errors.vnbTariff = VNB_ERR_TARIFF;
  }
  return errors;
}

/** The block as the run payload carries it. */
export function buildVirtualNetBillingPayload(block) {
  const src = block || {};
  const charge = toNumber(src.nettedChargeEurPerMwh);
  // Voltage levels travel only when entered, so a project without them sends
  // exactly what it sent before.
  const station = voltageLevel(src.stationVoltageLevel);
  const voltage = {};
  if (station) {
    voltage.stationVoltageLevel = station;
    ["lossUpliftMvPct", "lossUpliftLvPct"].forEach((key) => {
      const n = toNumber(src[key]);
      if (Number.isFinite(n)) voltage[key] = n;
    });
  }
  return {
    nettedChargeEurPerMwh: Number.isFinite(charge) ? charge : null,
    surplusCompensated: src.surplusCompensated !== false,
    ...voltage,
    supplies: (Array.isArray(src.supplies) ? src.supplies : []).map((s, i) => {
      const share = toNumber(s?.sharePct);
      const out = { name: String(s?.name || "").trim() || String(i + 1), sharePct: Number.isFinite(share) ? share : null };
      const level = station ? voltageLevel(s?.voltageLevel) : null;
      if (level) out.voltageLevel = level;
      const price = toNumber(s?.energyPriceEurKwh);
      if (s?.tariffMode === "own" && Number.isFinite(price)) out.energyPriceEurKwh = price;
      if (Array.isArray(s?.loadProfile?.values) && s.loadProfile.values.length) {
        out.loadProfile = {
          values: s.loadProfile.values,
          resolution: s.loadProfile.resolution || "hourly",
          source: s.loadProfile.source || "",
          use: true,
        };
      }
      return out;
    }),
  };
}
